import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { periverseTheme } from '@peritasai/ui-shared';
import { useAuthContext } from '../../features/auth';
import { useAuthStore } from '../../features/auth/stores/auth.store';

// Global flag to prevent multiple PKCE callback executions
let globalPKCEProcessing = false;

function AuthLoader() {
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: periverseTheme.palette.background.default }}
    >
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );
}

export const Route = createFileRoute('/auth/callback')({
  component: AuthCallbackPage,
  validateSearch: (search: Record<string, unknown>) => ({
    code: search.code as string | undefined,
    error: search.error as string | undefined,
    state: search.state as string | undefined,
  }),
});

function AuthCallbackPage() {
  const { code, error } = useSearch({ from: '/auth/callback' });
  const {
    handlePKCECallback,
    error: authError,
  } = useAuthContext();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);
  const [processError, setProcessError] = useState<string | null>(null);

  // Use refs to prevent multiple executions
  const hasProcessed = useRef(false);
  const isProcessing = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Early return if already processed or currently processing
    if (hasProcessed.current || isProcessing.current || globalPKCEProcessing) {
      console.log(
        'AuthCallbackPage: Already processed or processing, skipping...'
      );
      return;
    }

    // Early return if no code and no error (e.g. user navigated back to /auth/callback)
    if (!code && !error) {
      setProcessing(false);
      navigate({ to: '/', replace: true });
      return;
    }

    // Early return if there's an error from ZITADEL
    if (error) {
      console.log('AuthCallbackPage: Error from ZITADEL:', error);
      setProcessError(`Authorization failed: ${error}`);
      setProcessing(false);
      hasProcessed.current = true;
      return;
    }

    // Mark as processing immediately to prevent multiple executions
    isProcessing.current = true;
    hasProcessed.current = true;
    globalPKCEProcessing = true;

    console.log('AuthCallbackPage: Starting PKCE callback processing...');
    console.log(
      '🔍 PKCE CALLBACK DEBUG - Received code from ZITADEL URL:',
      code
    );

    const processCallback = async () => {
      try {
        setProcessing(true);
        setProcessError(null);

        // Get stored PKCE parameters
        const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
        const redirectUri =
          sessionStorage.getItem('pkce_redirect_uri') ||
          `${window.location.origin}/auth/callback`;

        if (!codeVerifier) {
          setProcessing(false);
          navigate({ to: '/', replace: true });
          return;
        }

        console.log('🔍 PKCE CALLBACK DEBUG - Code from ZITADEL:', code);
        console.log(
          '🔍 PKCE CALLBACK DEBUG - CodeVerifier from sessionStorage:',
          codeVerifier
        );
        console.log('🔍 PKCE CALLBACK DEBUG - RedirectUri:', redirectUri);

        console.log('Processing PKCE callback...', {
          code,
          codeVerifier,
          redirectUri,
        });

        // Handle the PKCE callback
        console.log('AuthCallbackPage: Calling handlePKCECallback...');
        if (!code) {
          throw new Error('No authorization code provided');
        }
        await handlePKCECallback(code, codeVerifier, redirectUri);
        console.log(
          'AuthCallbackPage: handlePKCECallback completed successfully'
        );

        console.log('PKCE callback successful, redirecting...');

        // Get return URL and redirect
        const returnUrl =
          sessionStorage.getItem('pkce_return_url') || '/';
        sessionStorage.removeItem('pkce_code_verifier');
        sessionStorage.removeItem('pkce_redirect_uri');
        sessionStorage.removeItem('pkce_return_url');

        // Clear URL parameters to prevent re-processing.
        // Preserve the existing history state so TanStack Router's __TSR_index
        // is not corrupted, which would break back-button navigation tracking.
        window.history.replaceState(
          window.history.state,
          document.title,
          window.location.pathname
        );

        // Wait a moment for the authentication state to fully update
        console.log('AuthCallbackPage: Waiting for auth state to stabilize...');
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Double-check authentication state before redirect
        const authState = useAuthStore.getState();
        console.log('AuthCallbackPage: Final auth state before redirect:', {
          isAuthenticated: authState.isAuthenticated,
          user: authState.user
            ? `${authState.user.username} (${authState.user.isAdmin ? 'ADMIN' : authState.user.permissions?.join(', ') ?? 'USER'})`
            : 'null',
          isLoading: authState.isLoading,
          error: authState.error,
        });

        // Redirect to the intended page
        console.log(`AuthCallbackPage: Redirecting to ${returnUrl}`);
        navigate({ to: returnUrl, replace: true });
      } catch (err) {
        console.error('PKCE callback processing failed:', err);
        setProcessError(
          err instanceof Error ? err.message : 'Unknown error occurred'
        );
        setProcessing(false);

        // If it's a 401 error, show a specific message
        if (err instanceof Error && err.message.includes('401')) {
          setProcessError(
            'Backend authentication error. Please contact the backend developer to fix the /auth/pkce/callback endpoint.'
          );
        }
      } finally {
        isProcessing.current = false;
        globalPKCEProcessing = false;
      }
    };

    // Set a timeout to prevent hanging
    timeoutRef.current = setTimeout(() => {
      if (isProcessing.current) {
        console.error('AuthCallbackPage: Timeout reached, stopping processing');
        setProcessError('Authentication timeout - please try again');
        setProcessing(false);
        isProcessing.current = false;
        globalPKCEProcessing = false;
      }
    }, 10000); // 10 second timeout

    processCallback();

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [code, error, handlePKCECallback, navigate]);

  // Show loading state while processing
  if (processing) {
    return <AuthLoader />;
  }

  // Show minimal error state - loader area + single Try again link
  if (processError || authError) {
    const errorMessage = processError || authError;

    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center"
        style={{ backgroundColor: periverseTheme.palette.background.default }}
      >
        <p className="text-gray-600 mb-4 text-sm">{errorMessage}</p>
        <a
          href="/"
          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
        >
          Try again
        </a>
      </div>
    );
  }

  // Fallback loading state
  return <AuthLoader />;
}
